import React, { useState, useRef, useEffect } from 'react';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import type { SupportedLanguage } from '../../types';

interface LanguageOption {
  code: SupportedLanguage;
  flagCode: string;
  name: string;
  short: string;
}

interface LanguageSwitcherProps {
  currentLanguage: SupportedLanguage;
  onLanguageChange: (lang: SupportedLanguage) => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ currentLanguage, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: LanguageOption[] = [
    { code: 'en', flagCode: 'gb', name: 'English', short: 'EN' },
    { code: 'es', flagCode: 'es', name: 'Español', short: 'ES' },
    { code: 'pt', flagCode: 'br', name: 'Português', short: 'PT' }
  ];

  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode: SupportedLanguage) => {
    onLanguageChange(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        className="flex items-center gap-1.5 py-1.5 px-2.5 bg-surface-primary border border-default rounded-lg text-content text-xs font-medium cursor-pointer transition-all duration-200 hover:bg-surface-elevated hover:border-strong"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`fi fi-${currentLang.flagCode} text-sm`} />
        <span className="text-xs font-semibold tracking-wide">{currentLang.short}</span>
        <ChevronDown size={12} className={`text-content-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-[calc(100%+0.25rem)] right-0 min-w-[130px] p-1 rounded-lg border border-default bg-surface-page shadow-xl z-dropdown animate-fade-in-down">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`flex items-center gap-2 w-full py-2 px-2.5 bg-transparent border-none rounded-md text-xs font-medium cursor-pointer transition-all duration-200 ${
                currentLanguage === lang.code 
                  ? 'bg-brand/20 text-primary' 
                  : 'text-content hover:bg-surface-primary'
              }`}
              onClick={() => handleSelect(lang.code)}
            >
              <span className={`fi fi-${lang.flagCode} text-sm`} />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
